import { styled } from "goober";
import { useCallback, useEffect, useState } from "react";
import { mediaTypeFromFileName } from "../../../shared/attachmentPolicy";
import { type ArchiveEntry, streamArchive } from "../../lib/archive";
import { formatBytes } from "../../lib/formatBytes";
import { FilePreviewModal } from "../AttachmentHoverPreview";
import { PressButton } from "../PressButton";
import { Text } from "../Text";
import { WavyLine } from "../WavyLine";
import { Binary } from "./Binary";
import { extensionFromMime, triggerDownload } from "./download";
import type { FileRenderControl } from "./types";

const MAX_INLINE_BYTES = 25 * 1024 * 1024;

type Props = {
  url: string;
  contentType: string;
  fileName?: string;
  bleed?: number;
  onControls?: (controls: FileRenderControl[]) => void;
};

// Browsers can't fetch blob: URLs under our CSP, so an entry's bytes are
// previewed via a base64 data: URL instead.
function toBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

// Files without a recognized extension are still worth previewing as text when
// they have no NUL bytes (source files, configs, logs), so default those to
// text/plain rather than an undownloadable octet-stream.
function looksTextual(bytes: Uint8Array): boolean {
  const n = Math.min(bytes.length, 1024);
  for (let i = 0; i < n; i++) if (bytes[i] === 0) return false;
  return n > 0;
}

const List = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  height: 100%;
  overflow: auto;
  padding: 2px;
`;

const Row = styled(PressButton)`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 0;
  border-radius: 8px;
  padding: 7px 10px;
  text-align: left;
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
  &:hover {
    background: var(--alpha4);
  }
`;

const RowName = styled("span")`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: var(--font-sans);
  font-size: 13px;
  line-height: 1.5;
`;

const RowSize = styled("span")`
  flex-shrink: 0;
  font-family: var(--font-sans);
  font-size: 12px;
  color: var(--text-secondary);
`;

const Center = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--text-secondary);
`;

type LoadState =
  | { status: "loading" }
  | { status: "too-large" }
  | { status: "error"; message: string }
  | { status: "ready"; entries: ArchiveEntry[] };

function useArchive(
  url: string,
  contentType: string,
  fileName?: string
): LoadState {
  const [state, setState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    setState({ status: "loading" });

    void (async () => {
      try {
        const res = await fetch(url, { credentials: "include", signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        if (Number(res.headers.get("Content-Length")) > MAX_INLINE_BYTES) {
          res.body?.cancel();
          setState({ status: "too-large" });
          return;
        }
        if (!res.body) throw new Error("Empty response");
        const final = await streamArchive(
          res.body,
          contentType,
          fileName,
          (entries) => {
            if (!signal.aborted && entries.length) {
              setState({ status: "ready", entries });
            }
          }
        );
        if (!signal.aborted) setState({ status: "ready", entries: final });
      } catch (err) {
        if (signal.aborted) return;
        setState({
          status: "error",
          message: err instanceof Error ? err.message : String(err),
        });
      }
    })();

    return () => controller.abort();
  }, [url, contentType, fileName]);

  return state;
}

type Preview = {
  fileName: string;
  mediaType: string;
  sizeBytes: number;
  url: string;
};

export function Archive({
  url,
  contentType,
  fileName,
  bleed,
  onControls,
}: Props) {
  const state = useArchive(url, contentType, fileName);
  const [preview, setPreview] = useState<Preview | null>(null);

  const download = useCallback(async () => {
    const res = await fetch(url, { credentials: "include" });
    const objectUrl = URL.createObjectURL(await res.blob());
    triggerDownload(
      objectUrl,
      fileName ?? `archive.${extensionFromMime(contentType)}`
    );
    URL.revokeObjectURL(objectUrl);
  }, [url, fileName, contentType]);

  useEffect(() => {
    onControls?.([
      {
        id: "download-archive",
        icon: "arrow-down",
        label: "Download archive",
        active: false,
        onToggle: () => void download(),
      },
    ]);
    return () => onControls?.([]);
  }, [onControls, download]);

  // A single shared lightbox keeps the listing cheap to render even for
  // archives with thousands of entries; only the clicked entry is decoded.
  const open = async (entry: ArchiveEntry) => {
    const bytes = await entry.extract();
    let mediaType = mediaTypeFromFileName(entry.path);
    if (mediaType === "application/octet-stream" && looksTextual(bytes)) {
      mediaType = "text/plain";
    }
    setPreview({
      fileName: entry.path,
      mediaType,
      sizeBytes: entry.size,
      url: `data:${mediaType};base64,${toBase64(bytes)}`,
    });
  };

  if (state.status === "loading") {
    return (
      <Center>
        <WavyLine size={24} opacity={0.4} />
      </Center>
    );
  }
  if (state.status === "too-large") {
    return <Binary url={url} contentType={contentType} fileName={fileName} />;
  }
  if (state.status === "error") {
    return (
      <Center>
        <Text variant="text-small">
          Unable to read archive: {state.message}
        </Text>
      </Center>
    );
  }
  if (state.entries.length === 0) {
    return (
      <Center>
        <Text variant="text-small">Empty archive</Text>
      </Center>
    );
  }

  return (
    <>
      <List
        style={
          bleed
            ? {
                margin: -bleed,
                width: `calc(100% + ${bleed * 2}px)`,
                height: `calc(100% + ${bleed * 2}px)`,
                padding: bleed,
              }
            : undefined
        }
      >
        {state.entries.map((entry) => (
          <Row key={entry.path} onPress={() => void open(entry)}>
            <RowName>{entry.path}</RowName>
            <RowSize>{formatBytes(entry.size)}</RowSize>
          </Row>
        ))}
      </List>
      <FilePreviewModal
        open={preview != null}
        onClose={() => setPreview(null)}
        fileName={preview?.fileName ?? ""}
        mediaType={preview?.mediaType ?? ""}
        sizeBytes={preview?.sizeBytes ?? 0}
        url={preview?.url ?? ""}
      />
    </>
  );
}
